// Status eines Export-Auftrags: Badge, Fortschritt, Download (signierte URL), Größe, Ablauf, Fehler.
import type { ExportJobDto, JobStatus } from '@lagerfeuer/shared';
import { formatBytes, formatDateTime } from '../format';
import { Badge, ProgressBar } from '../ui';
import { sameOriginUrl } from '../safe-url';

const STATUS: Record<JobStatus, { label: string; tone: 'accent' | 'warn' | 'ok' | 'danger' | 'neutral' }> = {
  PENDING: { label: 'Wartet', tone: 'warn' },
  RUNNING: { label: 'Läuft', tone: 'accent' },
  DONE: { label: 'Fertig', tone: 'ok' },
  FAILED: { label: 'Fehlgeschlagen', tone: 'danger' },
  EXPIRED: { label: 'Abgelaufen', tone: 'neutral' },
};

export function ExportStatusBadge({ status, testId }: { status: JobStatus; testId: string }) {
  const s = STATUS[status];
  return (
    <Badge tone={s.tone} testId={testId}>
      {s.label}
    </Badge>
  );
}

/** Detailblock; `testId` ist das Präfix (z. B. `exports-dialog` → `exports-dialog-download`). */
export function ExportStatus({ job, testId }: { job: ExportJobDto; testId: string }) {
  const active = job.status === 'PENDING' || job.status === 'RUNNING';
  const progress = Math.max(0, Math.min(100, Math.round(job.progress)));
  return (
    <div className="lfx-stack lfx-exp-status" data-testid={`${testId}-status`} data-status={job.status}>
      <div className="lfx-row">
        <ExportStatusBadge status={job.status} testId={`${testId}-status-badge`} />
        {active && (
          <span className="lfx-muted" aria-live="polite" data-testid={`${testId}-progress-text`}>
            {job.status === 'PENDING' ? 'In der Warteschlange …' : `${progress} %`}
          </span>
        )}
      </div>
      {active && <ProgressBar value={progress} testId={`${testId}-progress`} label="Export-Fortschritt" />}
      {job.status === 'DONE' && (
        <div className="lfx-row">
          {sameOriginUrl(job.downloadUrl) ? (
            <a className="lfx-btn lfx-btn--primary lfx-exp-download" href={sameOriginUrl(job.downloadUrl)!} download data-testid={`${testId}-download`}>
              Herunterladen
            </a>
          ) : (
            <span className="lfx-muted">Kein Download verfügbar.</span>
          )}
          <span className="lfx-muted" data-testid={`${testId}-size`}>
            {formatBytes(job.sizeBytes)}
          </span>
          {job.expiresAt && (
            <span className="lfx-muted" data-testid={`${testId}-expires`}>
              verfügbar bis {formatDateTime(job.expiresAt)}
            </span>
          )}
        </div>
      )}
      {job.status === 'FAILED' && (
        <div className="lfx-error" role="alert" data-testid={`${testId}-failure`}>
          Export fehlgeschlagen{job.error ? `: ${job.error}` : '.'}
        </div>
      )}
      {job.status === 'EXPIRED' && <div className="lfx-muted">Die Datei wurde nach Ablauf der Aufbewahrungszeit gelöscht.</div>}
    </div>
  );
}
